/**
 * Authentication API Service
 * 
 * Centralized API calls for authentication operations
 * Uses the centralized request utility from src/shared/utils/api.js
 */

import api from '../../../shared/utils/api';

/**
 * Login with email and password
 * @param {Object} credentials - User credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @returns {Promise<Object>} Authentication response with user data and token
 * @throws {Error} If authentication fails
 */
export const login = async ({ email, password }) => {
  try {
    const payload = {
      email,
      password,
    };

    // Note: Using fetch directly here to match legacy behavior
    // The legacy code uses fetch with credentials: 'include' for this endpoint
    const response = await fetch('/api/users/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payload }),
      credentials: 'include',
    });

    const data = await response.json();

    if (!data.success) {
      const error = new Error(data.message || 'Authentication failed');
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Login API error:', error);
    throw error;
  }
};

/**
 * Get server timezone
 * @returns {Promise<Object>} Timezone response with timeZone property
 * @throws {Error} If timezone fetch fails
 */
export const getTimezone = async () => {
  try {
    const response = await api.get('/api/timezone');
    return response;
  } catch (error) {
    console.error('Get timezone API error:', error);
    throw error;
  }
};

/**
 * Sign up a new user
 * @param {Object} userData - User registration data
 * @param {string} userData.name - User's full name
 * @param {string} userData.email - User's email
 * @param {string} userData.password - User's password
 * @returns {Promise<Object>} Signup response
 * @throws {Error} If signup fails
 */
export const signup = async ({ name, email, password }) => {
  try {
    const response = await fetch('/api/users/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payload: { name, email, password } }),
    });

    const data = await response.json();

    if (!data.success) {
      const error = new Error(data.message || 'Signup failed');
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Signup API error:', error);
    throw error;
  }
};

const authApi = {
  login,
  getTimezone,
  signup,
};

export default authApi;
